import { BillingDetails } from '@/models/billing/BillingDetails';
import { DatePipe, formatCurrency } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AppService } from '@services/app.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { Subject, takeUntil } from 'rxjs';
import * as CryptoJS from 'crypto-js';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Swal from 'sweetalert2';
import { saveAs } from 'file-saver';

@Component({
  selector: 'app-shop-bill-list-table',
  templateUrl: './shop-bill-list-table.component.html',
  styleUrl: './shop-bill-list-table.component.scss'
})
export class ShopBillListTableComponent implements OnInit {
  destroy$: Subject<boolean> = new Subject<boolean>();
  shopBillDetails: BillingDetails[] = [];
  getAllShopBillDetails: BillingDetails[] = [];
  searchText: any;
  productNameDetails: string[] = [];
  productCodeDetails: string[] = [];
  billDateDetails: any[] = [];
  billTypeDetails: string[] = [];
  paymentTypesDetails: string[] = [];
  exchangeTypesDetails: string[] = [];
  productName: string = '';
  productCode: string = '';
  billDate: string = '';
  billTypes: string = '';
  paymentTypes: string = '';
  exchangeType: string = '';
  private secretKey = "#EVOL-&$%$DF%SION*^%#^*F&%^DFSEYUJ";
  totalSaleAmount: number = 0;

  fromDate!: string;
  toDate!: string;
  todayMax!: string;

  startDate: number = 0;
  endDate: number = 0;
  userType: string = '';

  constructor(private appService: AppService, private toastr: ToastrService, private spinner: NgxSpinnerService,
    private datePipe: DatePipe, private router: Router) {
  }

  ngOnInit(): void {
    this.todayMax = this.formatDate(new Date());
    this.setLastWeekDates();
    this.getBillingDetailsByStartAndEndDate();
    this.userType = (localStorage.getItem('userType') || "")
  }


  // Auto set last 7 days
  setLastWeekDates() {
    const today = new Date();
    const lastWeek = new Date();

    lastWeek.setDate(today.getDate() - 7);

    this.fromDate = this.formatDate(lastWeek);
    this.toDate = this.formatDate(today);

    this.convertToEpoch();
  }

  // format yyyy-MM-dd
  formatDate(date: Date): string {
    return date.toISOString().split('T')[0];
  }

  // convert to epoch 13 digit
  convertToEpoch() {
    this.startDate = new Date(this.fromDate).getTime();
    this.endDate = new Date(this.toDate).getTime();
  }

  // when user change date
  onDate() {

    if (!this.fromDate || !this.toDate) return;

    if (new Date(this.fromDate) > new Date(this.toDate)) {
      this.toastr.error('Start date should not be greater than End date');
      return;
    }

    this.convertToEpoch();
    this.getBillingDetailsByStartAndEndDate();
  }


  getBillingDetailsByStartAndEndDate() {
    this.spinner.show();
    this.appService.getBillingDetailsByStartAndEndDate(this.startDate,this.endDate)
      .subscribe({
        next: (res) => {
          this.spinner.hide();
        this.getAllShopBillDetails = res.sort((a, b) => b.billId - a.billId);
        // get unique details for dropdowns  - productName, productCode, productCreationDate, productStatus
        this.productNameDetails = [...new Set(this.getAllShopBillDetails.flatMap(item => item.products.map(product => product.productName)))];
        this.productCodeDetails = [...new Set(this.getAllShopBillDetails.flatMap(item => item.products.map(product => product.productCode)))];
        this.exchangeTypesDetails = [...new Set(this.getAllShopBillDetails.map(product => product.exchangeStatus))];
        this.billDateDetails = [...new Set(this.getAllShopBillDetails.map(item =>
          item.createdAt ? new Date(item.createdAt).toLocaleDateString('en-GB') : '-'))];
        this.billTypeDetails = [...new Set(this.getAllShopBillDetails.map(item => item.billingType))];
        this.paymentTypesDetails = [...new Set(this.getAllShopBillDetails.map(item => item.paymentMethod))];
        this.shopBillDetails = this.getAllShopBillDetails;
        this.totalSaleAmount = this.shopBillDetails.filter(v => v.billingStatus === 'PAID').reduce((sum, item) => sum + (item.totalAmount || 0), 0);
        },
        error: (err) => {
          this.spinner.hide();
          this.toastr.error('Failed to load billing data');
          console.error(err);
        }
      });
  }

  // get product details
  getAllBillDetails() {
    this.spinner.show();
    this.appService.getAllBillDetails()
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.spinner.hide();
        this.getAllShopBillDetails = data.sort((a, b) => b.billId - a.billId);
        // get unique details for dropdowns  - productName, productCode, productCreationDate, productStatus
        this.productNameDetails = [...new Set(this.getAllShopBillDetails.flatMap(item => item.products.map(product => product.productName)))];
        this.productCodeDetails = [...new Set(this.getAllShopBillDetails.flatMap(item => item.products.map(product => product.productCode)))];
        this.exchangeTypesDetails = [...new Set(this.getAllShopBillDetails.map(product => product.exchangeStatus))];
        this.billDateDetails = [...new Set(this.getAllShopBillDetails.map(item =>
          item.createdAt ? new Date(item.createdAt).toLocaleDateString('en-GB') : '-'))];
        this.billTypeDetails = [...new Set(this.getAllShopBillDetails.map(item => item.billingType))];
        this.paymentTypesDetails = [...new Set(this.getAllShopBillDetails.map(item => item.paymentMethod))];
        this.shopBillDetails = this.getAllShopBillDetails;
        this.totalSaleAmount = this.shopBillDetails.filter(v => v.billingStatus === 'PAID').reduce((sum, item) => sum + (item.totalAmount || 0), 0);
      }, (error: any) => {
        this.spinner.hide();
        this.toastr.error(error.message || "Something went wrong, please try again...")
      });
  }

  getFormattedCurrency(amount: number): string {
    return Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2 }).format(amount);
  }

  getFormattedAmount(amount: number): string {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount);
  }

  printBill(billId: number, buyerType: string) {
    const encryptedBillId = CryptoJS.AES.encrypt(String(billId), this.secretKey).toString();
    const encryptedBuyerType = CryptoJS.AES.encrypt(String(buyerType), this.secretKey).toString();
    const encryptedScreenName = CryptoJS.AES.encrypt(String(""), this.secretKey).toString();
    // Navigate to bill page
    this.router.navigate(['/bill'], { queryParams: { billId: encryptedBillId, buyerType: encryptedBuyerType, screenName: encryptedScreenName } });
  }


  // Current select filter value set function
  updateDetails(event: any, filterType: string) {
    const value = event.target.value;

    switch (filterType) {
      case 'payment':
        this.paymentTypes = value !== 'Select Payment Type' ? value : 'all';
        break;
      case 'date':
        this.billDate = value !== 'Select Bill Date' ? value : 'all';
        break;
      case 'status':
        this.billTypes = value !== 'Select Bill Type' ? value : 'all';
        break;
      case 'exchange':
        this.exchangeType = value !== 'Select Exchange Type' ? value : 'all';
        break;
      default:
        break;
    }

    this.applyFilter();
  }

  applyFilter() {
    let filteredData = [...this.getAllShopBillDetails];

    if (this.productName && this.productName !== 'all') {
      filteredData = filteredData.filter(item =>
        item.products.some(product => product.productName === this.productName)
      );
    }

    if (this.productCode && this.productCode !== 'all') {
      filteredData = filteredData.filter(item =>
        item.products.some(product => product.productCode === this.productCode)
      );
    }

    if (this.paymentTypes && this.paymentTypes !== 'all') {
      filteredData = filteredData.filter(item =>
        item.paymentMethod === this.paymentTypes
      );
    }

    if (this.billDate && this.billDate !== 'all') {
      filteredData = filteredData.filter(item =>
        this.datePipe.transform(item.createdAt, 'dd/MM/yyyy') === this.billDate
      );
    }

    if (this.billTypes && this.billTypes !== 'all') {
      filteredData = filteredData.filter(item => item.billingType === this.billTypes);
    }

    if (this.exchangeType && this.exchangeType !== 'all') {
      filteredData = filteredData.filter(item => item.exchangeStatus === this.exchangeType);
    }


    this.productCodeDetails = [...new Set(filteredData.flatMap(item => item.products.map(product => product.productCode)))];
    this.billDateDetails = [...new Set(filteredData.map(item => this.datePipe.transform(item.createdAt, 'dd/MM/yyyy')))];
    this.billTypeDetails = [...new Set(filteredData.map(item => item.billingType))];
    this.exchangeTypesDetails = [...new Set(filteredData.map(item => item.exchangeStatus))];
    this.shopBillDetails = filteredData;
    this.totalSaleAmount = this.shopBillDetails.filter(v => v.billingStatus === 'PAID').reduce((sum, item) => sum + (item.totalAmount || 0), 0);
  }

updateAdminStatus(billId: number) {

    const JsonData = {
        billId: billId,
        adminStatus: 'APPROVED'
    };

    Swal.fire({
        title: 'Approve this bill?',
        text: 'This bill will be marked as Approved.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, Approve',
        cancelButtonText: 'No',
        allowOutsideClick: false
    }).then((result) => {

        if (!result.isConfirmed) {
            return;
        }

        this.spinner.show();

        this.appService.updateAdminStatus(JsonData)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (data:any) => {
                    this.spinner.hide();
                    this.toastr.success(
                        'Bill approved successfully.'
                    );
                    // Refresh billing list
                    this.getBillingDetailsByStartAndEndDate();
                },

                error: (error: any) => {
                    this.spinner.hide();
                    this.toastr.error(
                        error?.error?.message ||
                        'Failed to update admin status.'
                    );
                }
            });
    });
}

  // Remove filter function values
  resetReviewFilter() {
    this.searchText = '';
    this.productName = '';
    this.productCode = '';
    this.billDate = '';
    this.billTypes = '';
    this.paymentTypes = '';
    this.exchangeType = '';

    this.shopBillDetails = [...this.getAllShopBillDetails];
    this.productNameDetails = [...new Set(this.getAllShopBillDetails.flatMap(item => item.products.map(product => product.productName)))];
    this.productCodeDetails = [...new Set(this.getAllShopBillDetails.flatMap(item => item.products.map(product => product.productCode)))];
    this.exchangeTypesDetails = [...new Set(this.getAllShopBillDetails.map(product => product.exchangeStatus))];
    this.paymentTypesDetails = [...new Set(this.getAllShopBillDetails.map(item => item.paymentMethod))];
    this.billDateDetails = [...new Set(this.getAllShopBillDetails.map(item =>
      item.createdAt ? new Date(item.createdAt).toLocaleDateString('en-GB') : '-'
    ))];
    this.billTypeDetails = [...new Set(this.getAllShopBillDetails.map(item => item.billingType))];
  }

  //Download excel file
  downloadExcel() {
    this.spinner.show();
    const worksheetData = this.shopBillDetails.filter(v => v.billingStatus === 'PAID').map((report, index) => ({
      'S.No': index + 1,
      'Customer Name': report?.customerName || '-',
      'Phone Number': report?.phoneNumber || '-',
      'City': report?.city || '-',
      'Bill Type': report?.billType || '-',
      'Bill Amount': (report?.subTotal ? report.subTotal.toFixed(2) : '0.00'),
      'Discount': (report?.discount ? report.discount.toFixed(2) : '0.00'),
      'Paypal Amount': (report?.totalAmount ? report.totalAmount.toFixed(2) : '0.00'),
      'Bill Date': report?.createdAt ? new Date(report.createdAt).toLocaleDateString('en-GB') : '-',
    }));

    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Filtered Reports');

    const filename = `Shop bill report, ${new Date().toLocaleDateString()}.xlsx`;

    // Check for issues with Excel file creation
    try {
      XLSX.writeFile(workbook, filename);
      this.spinner.hide();
      this.toastr.success('Excel file downloaded successfully!');
    } catch (error : any) {
      this.spinner.hide();
      this.toastr.error('Error during file export:', error);
      console.error('Error during file export:', error);
    }
  }

  downloadPDF() {
  this.spinner.show();
  const doc = new jsPDF('p', 'mm', 'a4');

  // Title
  doc.setFontSize(16);
  doc.text('Lovely Fashion - Madurai', 105, 15, { align: 'center' });

  // Define table headers
  const headers = [
    ['S.No', 'Customer Name', 'Phone Number', 'City', 'Bill Type', 'Bill Amount', 'Discount', 'Paypal Amount', 'Bill Date']
  ];

  let currentY = 25;

  // 1. Generate PAID bill table
  const paidBills = this.shopBillDetails.filter(v => v.billingStatus === 'PAID');
  const paidData = paidBills.map((report, index) => [
    index + 1,
    report?.customerName || '-',
    report?.phoneNumber || '-',
    report?.city || '-',
    report?.billType || '-',
    (report?.subTotal ? report.subTotal.toFixed(2) : '0.00'),
    (report?.discount ? report.discount.toFixed(2) : '0.00'),
    (report?.totalAmount ? report.totalAmount.toFixed(2) : '0.00'),
    report?.createdAt ? new Date(report.createdAt).toLocaleDateString('en-GB') : '-',
  ]);

  autoTable(doc, {
    head: headers,
    body: paidData,
    startY: currentY,
    theme: 'grid',
    styles: { fontSize: 10, halign: 'center', valign: 'middle' },
    headStyles: { fillColor: [223, 193, 255] },
    alternateRowStyles: { fillColor: [240, 240, 240] },
    columnStyles: {
      0: { cellWidth: 10 }, 1: { cellWidth: 30 }, 2: { cellWidth: 25 },
      3: { cellWidth: 20 }, 4: { cellWidth: 20 }, 5: { cellWidth: 17 },
      6: { cellWidth: 20 }, 7: { cellWidth: 25 }, 8: { cellWidth: 25 },
    },
    didDrawPage: function (data) {
     if (data.cursor) {
    currentY = data.cursor.y + 10;
  } // Get Y after table ends
    }
  });

  // 2. Add heading for cancelled bills
  doc.setFontSize(14);
  doc.text('Cancelled Bill Details', 105, currentY, { align: 'center' });
  currentY += 5;

  // 3. Generate CANCELLED bill table
  const cancelledBills = this.shopBillDetails.filter(v => v.billingStatus === 'CANCELLED');
  const cancelledData = cancelledBills.map((report, index) => [
    index + 1,
    report?.customerName || '-',
    report?.phoneNumber || '-',
    report?.city || '-',
    report?.billType || '-',
    (report?.subTotal ? report.subTotal.toFixed(2) : '0.00'),
    (report?.discount ? report.discount.toFixed(2) : '0.00'),
    (report?.totalAmount ? report.totalAmount.toFixed(2) : '0.00'),
    report?.createdAt ? new Date(report.createdAt).toLocaleDateString('en-GB') : '-',
  ]);

  autoTable(doc, {
    head: headers,
    body: cancelledData,
    startY: currentY,
    theme: 'grid',
    styles: { fontSize: 10, halign: 'center', valign: 'middle' },
    headStyles: { fillColor: [255, 165, 165] }, // Red-ish header for cancelled
    alternateRowStyles: { fillColor: [245, 245, 245] },
    columnStyles: {
      0: { cellWidth: 10 }, 1: { cellWidth: 30 }, 2: { cellWidth: 25 },
      3: { cellWidth: 20 }, 4: { cellWidth: 20 }, 5: { cellWidth: 17 },
      6: { cellWidth: 20 }, 7: { cellWidth: 25 }, 8: { cellWidth: 25 },
    }
  });

  // Save PDF
  doc.save(`Shop_Bill_Report_${new Date().toLocaleDateString()}.pdf`);
  this.spinner.hide();
  this.toastr.success('PDF file downloaded successfully!');
}


  editShow(id: any) {
    const billId = btoa(id);
    localStorage.setItem('billId', billId);
    this.router.navigate(["/main/invoice"]);
  }

  cancelPopup(id: number) {
    Swal.fire({
      title: "Are you sure you want to cancel this bill?",
      icon: 'warning',
      showDenyButton: true,
      showCancelButton: false,
      confirmButtonText: "Yes",
      denyButtonText: "No",
      allowOutsideClick: false,
      allowEscapeKey: false,
    }).then((result) => {
      if (result.isConfirmed) {
        this.cancelStatusUpdated(id)
      }
    });
  }

  cancelStatusUpdated(id: number) {
    const JsonData = {
      "billingId": id,
      "billingStatus": "CANCELLED"
    }
    this.spinner.show();
    this.appService.billStatusUpdated(JsonData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.spinner.hide();
          this.toastr.success("Bill status updated successfully.");
          this.getAllBillDetails();
        },
        error: (error: any) => {
          this.spinner.hide();
          this.toastr.error(error.error.message || "Something went wrong. Please try again.");
        }
      });
  }

  getGrandTotal(bill: any): number {
  const withGst = Number(bill?.withGst) || 0;
  const manualDiscount = Number(bill?.manualDiscountAmount) || 0;
  const discount = Number(bill?.discount) || 0;

  return withGst - (manualDiscount + discount);
}

downloadGSTReport(): void {

  let serialNo = 1;

  const gstSalesData: any[] = [];

  this.shopBillDetails.forEach((bill: any) => {

    if (!bill.products || bill.products.length === 0) {

      gstSalesData.push({

        'S.No': serialNo++,

        'Invoice No': bill.invoiceNumber,

        'Invoice Date': bill.invoiceDate,

        'Customer Name': bill.customerName,

        'State': bill.state,

        'Billing Type': bill.billingType,

        'HSN Code': '',

        'Product Name': '',

        'Quantity': '',

        'Unit Price': '',

        'Taxable Value': bill.withoutGst || 0,

        'CGST %': bill.igstValue > 0 ? 0 : 2.5,

        'CGST Amount': bill.igstValue > 0 ? 0 : bill.cgstValue,

        'SGST %': bill.igstValue > 0 ? 0 : 2.5,

        'SGST Amount': bill.igstValue > 0 ? 0 : bill.sgstValue,

        'IGST %': bill.igstValue > 0 ? 5 : 0,

        'IGST Amount': bill.igstValue || 0,

        'Total GST': bill.gstValue || 0,

        'Shipping Charge': bill.shippingAmount || 0,

        'Discount':
          (bill.discount || 0) +
          (bill.manualDiscountAmount || 0),

        'Invoice Value': bill.totalAmount || 0,

        'Status': bill.billingStatus
      });

      return;
    }

    bill.products.forEach((product: any) => {

      gstSalesData.push({

        'S.No': serialNo++,

        'Invoice No': bill.invoiceNumber,

        'Invoice Date': bill.invoiceDate,

        'Customer Name': bill.customerName,

        'State': bill.state,

        'Billing Type': bill.billingType,

        'HSN Code': product.hsnCode || '',

        'Product Name': product.productName || '',

        'Quantity': product.quantity || 0,

        // 'Unit Price': product.unitPrice || 0,

        'Taxable Value + Shipping Charge': bill.withoutGst || 0,
        
        // '': bill.shippingAmount || 0,

        'CGST %': bill.igstValue > 0 ? 0 : 2.5,

        'CGST Amount': bill.igstValue > 0 ? 0 : bill.cgstValue,

        'SGST %': bill.igstValue > 0 ? 0 : 2.5,

        'SGST Amount': bill.igstValue > 0 ? 0 : bill.sgstValue,

        'IGST %': bill.igstValue > 0 ? 5 : 0,

        'IGST Amount': bill.igstValue || 0,

        'Total GST': bill.gstValue || 0,

        'Discount':
          (bill.discount || 0) +
          (bill.manualDiscountAmount || 0),

        'Invoice Value': bill.totalAmount || 0,

        'Status': bill.billingStatus
      });

    });

  });

  const worksheet: XLSX.WorkSheet =
    XLSX.utils.json_to_sheet(gstSalesData);

  const workbook: XLSX.WorkBook = {
    Sheets: {
      'GST Report': worksheet
    },
    SheetNames: ['GST Report']
  };

  const excelBuffer: any = XLSX.write(
    workbook,
    {
      bookType: 'xlsx',
      type: 'array'
    }
  );

  const blob = new Blob(
    [excelBuffer],
    {
      type:
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'
    }
  );

  saveAs(blob, 'GST_Report.xlsx');
}

}
